import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Container, Button, Form, Modal, Spinner, Dropdown, Row, Col, Pagination, InputGroup, Alert } from 'react-bootstrap';
import { 
  FaSearch, FaTimesCircle, FaPlus, FaTrash, FaEdit, 
  FaExclamationTriangle, FaCalendarTimes, 
  FaCalendarAlt, FaBoxes 
} from 'react-icons/fa';
import { get, post, put, del } from '../utils/api';
import { useCurrency } from '../utils/CurrencyContext';
import './BatchManagement.css';
import ProgressDialog from './ProgressDialog';
import _ from 'lodash';
import NoDataCard from './NoDataCard';

const BatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const hasMountedRef = useRef(false);
  
  // Filter and Sorting state
  const [filterExpiry, setFilterExpiry] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [sortBy, setSortBy] = useState('expiry-asc');
  const [currentPage, setCurrentPage] = useState(1);

  const [form, setForm] = useState({
    medicine_id: '',
    batch_number: '',
    expiry_date: '',
    purchase_price: '',
    selling_price: '',
    stock_quantity: '',
    supplier_id: '',
  });

  const fetchBatches = useCallback(async (batchNumber = '', showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setIsSearching(true);
      }
      const response = await get(batchNumber ? `/batches?batch_number=${batchNumber}&limit=10000` : '/batches?limit=10000');
      const batchesData = response?.data?.data ?? response?.data ?? [];
      setBatches(batchesData || []);
    } catch (err) {
      setError('Failed to fetch batches.');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
      setIsSearching(false);
    }
  }, []);

  const fetchMedicineAndSuppliers = useCallback(async () => {
    try {
      // Only fetch suppliers on mount. Medicines will be searched on-demand.
      const suppliersResponse = await get('/suppliers');
      const suppliersData = suppliersResponse?.data?.data ?? suppliersResponse?.data ?? [];
      setSupplierList(suppliersData);
    } catch (err) {
      setError('Failed to fetch suppliers.');
    }
  }, []);

  // Initial load: fetch batches and reference data on mount
  useEffect(() => {
    fetchBatches();
    fetchMedicineAndSuppliers();
  }, [fetchBatches, fetchMedicineAndSuppliers]);

  const debouncedFetch = useMemo(() => {
    return _.debounce((query) => {
      fetchBatches(query, false);
    }, 500);
  }, [fetchBatches]);

  useEffect(() => {
    // Only run debounced search when user actually types a query.
    // Prevents duplicate fetch on initial mount (full-page load then debounced search).
    if (!searchQuery) return;
    debouncedFetch(searchQuery);
    return () => {
      if (debouncedFetch.cancel) debouncedFetch.cancel();
    };
  }, [searchQuery, debouncedFetch]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (searchQuery === '') {
      fetchBatches();
    }
  }, [searchQuery, fetchBatches]);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);

  const { currencySymbol } = useCurrency();

  // Dynamic stats calculation
  const stats = useMemo(() => {
    const today = new Date();
    let total = batches.length;
    let expired = 0;
    let nearExpiry = 0;
    let totalStock = 0;
    
    batches.forEach(b => {
      const stock = parseInt(b.stock_quantity || 0);
      totalStock += stock;
      
      const expDate = b.expiry_date ? new Date(b.expiry_date) : null;
      if (expDate) {
        const daysLeft = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
        if (daysLeft < 0) {
          expired++;
        } else if (daysLeft <= 90) {
          nearExpiry++;
        }
      }
    });
    
    return { total, expired, nearExpiry, totalStock };
  }, [batches]);

  // Client-side filtering & sorting
  const filteredBatches = useMemo(() => {
    let list = [...batches];
    
    // Apply expiry filter
    if (filterExpiry !== 'all') {
      const today = new Date();
      list = list.filter(b => {
        const expDate = b.expiry_date ? new Date(b.expiry_date) : null;
        if (!expDate) return false;
        const daysLeft = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
        if (filterExpiry === 'expired') {
          return daysLeft < 0;
        } else if (filterExpiry === 'near-expiry') {
          return daysLeft >= 0 && daysLeft <= 90;
        } else if (filterExpiry === 'active') {
          return daysLeft > 90;
        }
        return true;
      });
    }
    
    // Apply stock filter
    if (filterStock !== 'all') {
      list = list.filter(b => {
        const stock = parseInt(b.stock_quantity || 0);
        if (filterStock === 'low-stock') {
          return stock < 50 && stock > 0;
        } else if (filterStock === 'in-stock') {
          return stock >= 50;
        }
        return true;
      });
    }
    
    // Apply sorting
    list.sort((a, b) => {
      if (sortBy === 'expiry-asc') {
        const dateA = a.expiry_date ? new Date(a.expiry_date) : new Date(0);
        const dateB = b.expiry_date ? new Date(b.expiry_date) : new Date(0);
        return dateA - dateB;
      }
      if (sortBy === 'expiry-desc') {
        const dateA = a.expiry_date ? new Date(a.expiry_date) : new Date(0);
        const dateB = b.expiry_date ? new Date(b.expiry_date) : new Date(0);
        return dateB - dateA;
      }
      if (sortBy === 'stock-desc') {
        return (parseInt(b.stock_quantity) || 0) - (parseInt(a.stock_quantity) || 0);
      }
      if (sortBy === 'stock-asc') {
        return (parseInt(a.stock_quantity) || 0) - (parseInt(b.stock_quantity) || 0);
      }
      if (sortBy === 'name-asc') {
        return (a.medicine_name || '').localeCompare(b.medicine_name || '');
      }
      return 0;
    });
    
    return list;
  }, [batches, filterExpiry, filterStock, sortBy]);

  // Client-side pagination config
  const itemsPerPage = 8;
  const totalPages = Math.ceil(filteredBatches.length / itemsPerPage);
  
  const paginatedBatches = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredBatches.slice(start, start + itemsPerPage);
  }, [filteredBatches, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // UOM dynamic stock formatter
  const formatBatchStock = (batch) => {
    const stock = parseInt(batch.stock_quantity || 0);
    const boxConversion = parseInt(batch.box_conversion) || 1;
    const stripConversion = parseInt(batch.strip_conversion) || 1;
    const baseUnit = batch.unit_name || 'piece';
    
    if (boxConversion > 1 && stock >= boxConversion) {
      const boxes = Math.floor(stock / boxConversion);
      const rem = stock % boxConversion;
      if (rem === 0) return `${boxes} box${boxes > 1 ? 'es' : ''}`;
      if (stripConversion > 1 && rem >= stripConversion) {
        const strips = Math.floor(rem / stripConversion);
        const pieces = rem % stripConversion;
        return `${boxes} box${boxes > 1 ? 'es' : ''}, ${strips} strip${strips > 1 ? 's' : ''} ${pieces > 0 ? `+ ${pieces} ${baseUnit}s` : ''}`;
      }
      return `${boxes} box${boxes > 1 ? 'es' : ''} + ${rem} ${baseUnit}s`;
    }
    
    if (stripConversion > 1 && stock >= stripConversion) {
      const strips = Math.floor(stock / stripConversion);
      const pieces = stock % stripConversion;
      if (pieces === 0) return `${strips} strip${strips > 1 ? 's' : ''}`;
      return `${strips} strip${strips > 1 ? 's' : ''} + ${pieces} ${baseUnit}s`;
    }
    
    return `${stock} ${baseUnit}s`;
  };

  const handleShowModal = (batch = null) => {
    setCurrentBatch(batch);
    setForm(
      batch
        ? {
          medicine_id: batch.medicine_id,
          batch_number: batch.batch_number,
          expiry_date: batch.expiry_date.split('T')[0],
          purchase_price: batch.purchase_price,
          selling_price: batch.selling_price,
          stock_quantity: batch.stock_quantity,
          supplier_id: batch.supplier_id,
        }
        : {
          medicine_id: '',
          batch_number: '',
          expiry_date: '',
          purchase_price: '',
          selling_price: '',
          stock_quantity: '',
          supplier_id: '',
        }
    );
    setShowModal(true);
  };



  // Lightweight searchable select for medicines with debounced remote search
  const SearchableMedicineSelect = ({ medicines = [], value, onChange, placeholder = 'Select Medicine' }) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [remoteResults, setRemoteResults] = useState(null);
    const [loadingRemote, setLoadingRemote] = useState(false);

    // Dynamic resolution of initial value/id
    useEffect(() => {
      if (!value) {
        setQuery('');
        return;
      }
      
      // If we already have the name matching in remoteResults, use it
      const matched = remoteResults?.find(m => String(m.medicine_id) === String(value));
      if (matched) {
        setQuery(matched.name);
        return;
      }

      // Otherwise fetch the specific medicine details from the API
      let active = true;
      const fetchSelectedName = async () => {
        try {
          const res = await get(`/medicines/${value}`);
          if (active && res && res.success && res.data) {
            setQuery(res.data.name);
          }
        } catch (e) {
          console.error("Failed to resolve selected medicine name:", e);
        }
      };
      fetchSelectedName();
      return () => { active = false; };
    }, [value]);

    // Debounced remote search
    useEffect(() => {
      const q = (query || '').trim();
      if (!q || q.length < 2) {
        setRemoteResults(null);
        setLoadingRemote(false);
        return;
      }

      let cancelled = false;
      setLoadingRemote(true);
      const timer = setTimeout(async () => {
        try {
          const res = await get(`/medicines?q=${encodeURIComponent(q)}&status=active`);
          if (cancelled) return;
          if (res && res.success) {
            setRemoteResults(res.data || []);
          } else {
            setRemoteResults([]);
          }
        } catch (err) {
          console.error('Medicine search failed', err);
          setRemoteResults([]);
        } finally {
          if (!cancelled) setLoadingRemote(false);
        }
      }, 300);

      return () => { cancelled = true; clearTimeout(timer); };
    }, [query]);

    const filtered = remoteResults !== null
      ? remoteResults
      : (query ? medicines.filter(m => (m.name || '').toLowerCase().includes(query.toLowerCase()) || (m.manufacturer || '').toLowerCase().includes(query.toLowerCase())) : medicines.slice(0, 8));

    return (
      <div className="searchable-select" style={{ position: 'relative' }}>
        <input
          type="text"
          className="form-control batch-modal-input"
          placeholder={placeholder}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          aria-autocomplete="list"
        />
        {open && (
          <ul className="searchable-options list-group position-absolute shadow-sm" style={{ zIndex: 2000, maxHeight: 240, overflowY: 'auto', width: '100%' }}>
            {loadingRemote && <li className="list-group-item">Searching...</li>}
            {!loadingRemote && filtered && filtered.length === 0 && <li className="list-group-item">No matches</li>}
            {!loadingRemote && filtered && filtered.map(m => (
              <li
                key={m.medicine_id}
                className="list-group-item list-group-item-action"
                onMouseDown={() => { onChange(String(m.medicine_id)); setQuery(m.name); setOpen(false); }}
                title={m.manufacturer || ''}
                style={{ cursor: 'pointer' }}
              >
                <div className="fw-bold">{m.name}</div>
                <small className="text-muted">{m.manufacturer}</small>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentBatch(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (currentBatch) {
        await put(`/batches/${currentBatch.batch_id}`, form);
      } else {
        await post('/batches', form);
      }
      fetchBatches();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save batch.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (batchId) => {
    if (window.confirm('Are you sure you want to delete this batch?')) {
      try {
        await del(`/batches/${batchId}`);
        fetchBatches();
      } catch (err) {
        setError('Failed to delete batch.');
      }
    }
  };

  return (
    <>
      <Container fluid className="batch-container px-4 px-sm-5">
        {/* Header Title Section */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-white mb-1">Batch Management</h2>
            <p className="text-muted">Monitor drug batches, manage expiration tracking, and check packaging conversions.</p>
          </div>
          <Button variant="primary" onClick={() => handleShowModal()} className="add-batch-btn card-hover py-2 px-3 d-flex align-items-center gap-2">
            <FaPlus /> Add Batch
          </Button>
        </div>

        {/* Stats Row */}
        <Row className="g-3 mb-4">
          <Col xs={6} md={3}>
            <div className="stat-card total-card">
              <div className="stat-icon-wrapper"><FaBoxes /></div>
              <div className="stat-details">
                <span className="stat-value">{stats.total}</span>
                <span className="stat-label">Total Batches</span>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="stat-card expired-card">
              <div className="stat-icon-wrapper"><FaCalendarTimes /></div>
              <div className="stat-details">
                <span className="stat-value">{stats.expired}</span>
                <span className="stat-label">Expired Batches</span>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="stat-card warning-card">
              <div className="stat-icon-wrapper"><FaCalendarAlt /></div>
              <div className="stat-details">
                <span className="stat-value">{stats.nearExpiry}</span>
                <span className="stat-label">Near Expiry (&lt;90d)</span>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="stat-card stock-card">
              <div className="stat-icon-wrapper"><FaExclamationTriangle /></div>
              <div className="stat-details">
                <span className="stat-value">{stats.totalStock}</span>
                <span className="stat-label">Total Base Stock</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Unified Filters Control Bar */}
        <div className="control-bar-wrapper mb-3 py-2 px-3 glassmorphic-panel">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={3}>
              <InputGroup className="search-input-group compact">
                <InputGroup.Text className="bg-transparent border-end-0 py-1 px-2">
                  <FaSearch className="text-muted" style={{ fontSize: '0.85rem' }} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search batch number..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="border-start-0 ps-0 py-1 search-input-field text-sm"
                  style={{ fontSize: '0.85rem', height: '36px' }}
                />
                {searchQuery && (
                  <Button variant="link" onClick={() => { setSearchQuery(''); setCurrentPage(1); }} className="clear-search-btn text-muted border-start-0 py-0 px-2">
                    <FaTimesCircle style={{ fontSize: '0.85rem' }} />
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col xs={4} md={3}>
              <Dropdown className="w-100">
                <Dropdown.Toggle 
                  variant="outline-secondary" 
                  className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm"
                  style={{ fontSize: '0.85rem', height: '36px', background: 'rgba(0, 0, 0, 0.15)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', color: 'var(--text-color)', textAlign: 'left' }}
                >
                  {filterExpiry === 'expired' ? 'Expiry: Expired' : filterExpiry === 'near-expiry' ? 'Expiry: Near Expiry' : filterExpiry === 'active' ? 'Expiry: Active' : 'Expiry: All'}
                </Dropdown.Toggle>
                <Dropdown.Menu className="filter-dropdown-menu w-100">
                  <Dropdown.Item onClick={() => { setFilterExpiry('all'); setCurrentPage(1); }}>Expiry: All</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setFilterExpiry('expired'); setCurrentPage(1); }}>Expiry: Expired</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setFilterExpiry('near-expiry'); setCurrentPage(1); }}>Expiry: Near Expiry</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setFilterExpiry('active'); setCurrentPage(1); }}>Expiry: Active</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col xs={4} md={3}>
              <Dropdown className="w-100">
                <Dropdown.Toggle 
                  variant="outline-secondary" 
                  className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm"
                  style={{ fontSize: '0.85rem', height: '36px', background: 'rgba(0, 0, 0, 0.15)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', color: 'var(--text-color)', textAlign: 'left' }}
                >
                  {filterStock === 'low-stock' ? 'Stock: Low Stock' : filterStock === 'in-stock' ? 'Stock: In Stock' : 'Stock: All'}
                </Dropdown.Toggle>
                <Dropdown.Menu className="filter-dropdown-menu w-100">
                  <Dropdown.Item onClick={() => { setFilterStock('all'); setCurrentPage(1); }}>Stock: All</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setFilterStock('low-stock'); setCurrentPage(1); }}>Stock: Low Stock</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setFilterStock('in-stock'); setCurrentPage(1); }}>Stock: In Stock</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col xs={4} md={3}>
              <Dropdown className="w-100">
                <Dropdown.Toggle 
                  variant="outline-secondary" 
                  className="filter-dropdown-toggle w-100 d-flex justify-content-between align-items-center text-sm"
                  style={{ fontSize: '0.85rem', height: '36px', background: 'rgba(0, 0, 0, 0.15)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: '8px', color: 'var(--text-color)', textAlign: 'left' }}
                >
                  {sortBy === 'expiry-desc' ? 'Sort: Expiry (Latest)' : sortBy === 'stock-desc' ? 'Sort: Stock (High)' : sortBy === 'stock-asc' ? 'Sort: Stock (Low)' : sortBy === 'name-asc' ? 'Sort: Name (A-Z)' : 'Sort: Expiry (Soonest)'}
                </Dropdown.Toggle>
                <Dropdown.Menu className="filter-dropdown-menu w-100">
                  <Dropdown.Item onClick={() => { setSortBy('expiry-asc'); setCurrentPage(1); }}>Sort: Expiry (Soonest)</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setSortBy('expiry-desc'); setCurrentPage(1); }}>Sort: Expiry (Latest)</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setSortBy('stock-desc'); setCurrentPage(1); }}>Sort: Stock (High)</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setSortBy('stock-asc'); setCurrentPage(1); }}>Sort: Stock (Low)</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setSortBy('name-asc'); setCurrentPage(1); }}>Sort: Name (A-Z)</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </div>

        {isSearching && (
          <div className="d-flex justify-content-center align-items-center my-3">
            <ProgressDialog show={isSearching} message="Searching..." inline />
          </div>
        )}

        {error && <p className="text-danger">{error}</p>}
        <ProgressDialog show={loading && !batches.length} message="Loading batches..." />

        {/* Main Grid View */}
        <div className="batches-grid">
          {paginatedBatches.length === 0 && !loading && !isSearching ? (
            <div className="grid-no-data py-5 text-center w-100">
              <NoDataCard
                title="No Batches Found"
                message="Adjust filters or query to find active batches."
                icon="📦"
                actionButton={
                  <Button variant="primary" onClick={() => handleShowModal()} className="card-hover">
                    <FaPlus /> Add New Batch
                  </Button>
                }
              />
            </div>
          ) : (
            paginatedBatches.map((batch) => {
              const expiryDate = batch.expiry_date ? new Date(batch.expiry_date) : null;
              const daysLeft = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
              const expiryLabel = daysLeft !== null ? (daysLeft < 0 ? 'Expired' : `${daysLeft} Days`) : 'N/A';
              const formattedExpiryDate = expiryDate ? expiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
              
              let expiryBadgeClass = 'expiry-safe';
              if (daysLeft !== null) {
                if (daysLeft < 0) expiryBadgeClass = 'expiry-expired';
                else if (daysLeft <= 90) expiryBadgeClass = 'expiry-warning';
              }

              let stockBadgeClass = 'stock-safe';
              const stockVal = parseInt(batch.stock_quantity || 0);
              if (stockVal <= 0) stockBadgeClass = 'stock-out';
              else if (stockVal < 50) stockBadgeClass = 'stock-low';

              return (
                <div className={`batch-card card-hover ${expiryBadgeClass}`} key={batch.batch_id}>
                  <div className="batch-card-header mb-2">
                    <h4 className="batch-title" title={batch.medicine_name}>{batch.medicine_name || 'Unknown Medicine'}</h4>
                    <span className={`expiry-pill ${expiryBadgeClass}`}>{expiryLabel}</span>
                  </div>
                  
                  <div className="batch-meta-row mb-3">
                    <span className="batch-num-tag">{batch.batch_number}</span>
                    <span className="batch-supplier-tag" title={batch.supplier_name}>{batch.supplier_name || '—'}</span>
                  </div>

                  <div className="batch-card-body mb-3">
                    <div className="batch-info-line">
                      <span className="info-label">Expiry Date</span>
                      <span className={`info-value ${daysLeft !== null && daysLeft < 0 ? 'expired-text' : ''}`}>{formattedExpiryDate}</span>
                    </div>
                    <div className="batch-info-line">
                      <span className="info-label">Stock Status</span>
                      <span className={`info-value fw-bold ${stockBadgeClass}`}>{formatBatchStock(batch)}</span>
                    </div>
                    <div className="batch-info-line">
                      <span className="info-label">Buy / Sell Price</span>
                      <span className="info-value text-primary fw-semibold">
                        {currencySymbol}{parseFloat(batch.purchase_price || 0).toFixed(2)} / {currencySymbol}{parseFloat(batch.selling_price || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="batch-card-actions">
                    <Button size="sm" onClick={() => handleShowModal(batch)} className="edit-btn-batch card-hover">
                      <FaEdit /> Edit
                    </Button>
                    <Button size="sm" onClick={() => handleDelete(batch.batch_id)} className="delete-btn-batch card-hover">
                      <FaTrash /> Delete
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination control */}
        {filteredBatches.length > 0 && totalPages > 1 && (
          <Pagination className="custom-pagination justify-content-center mt-4">
            <Pagination.Prev onClick={() => handlePageChange(Math.max(1, currentPage - 1))} />
            {[...Array(totalPages).keys()].map((number) => (
              <Pagination.Item
                key={number + 1}
                active={number + 1 === currentPage}
                onClick={() => handlePageChange(number + 1)}
              >
                {number + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} />
          </Pagination>
        )}
      </Container>

      <Modal show={showModal} onHide={handleCloseModal} scrollable className="batch-modal-modern">
        <Modal.Header closeButton className="batch-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="batch-modal-icon">
              <FaBoxes />
            </div>
            <div>
              <Modal.Title className="batch-modal-title mb-0">
                {currentBatch ? 'Edit Batch Configuration' : 'Add Inventory Batch'}
              </Modal.Title>
              <div className="batch-modal-subtitle">
                {currentBatch ? `Modifying Batch #${currentBatch.batch_number}` : 'Add a new product stock batch with expiry dates and pricing'}
              </div>
            </div>
          </div>
        </Modal.Header>

        <Form onSubmit={handleSubmit} className="batch-modal-form-wrapper">
          <Modal.Body className="batch-modal-body p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            
            {/* Section 1: Batch Identity & Expiry */}
            <div className="batch-form-section mb-4">
              <div className="batch-section-title mb-3">
                <span className="section-badge">1</span> Batch Identification
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label className="batch-field-label">Select Medicine Product <span className="text-danger">*</span></Form.Label>
                <SearchableMedicineSelect
                  medicines={[]}
                  value={form.medicine_id}
                  onChange={(medId) => setForm({ ...form, medicine_id: medId })}
                  placeholder="Type medicine name or manufacturer..."
                />
                <div className="batch-modal-hint mt-1">Select the medicine parent product from the catalog.</div>
              </Form.Group>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="batch-field-label">Batch Code/Number <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="text" 
                      name="batch_number" 
                      value={form.batch_number} 
                      onChange={handleChange} 
                      placeholder="e.g. BT-98102"
                      required 
                      className="batch-modal-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="batch-field-label">Expiry Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="date" 
                      name="expiry_date" 
                      value={form.expiry_date} 
                      onChange={handleChange} 
                      required 
                      className="batch-modal-input"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Section 2: Ledger, Costing & Supplier */}
            <div className="batch-form-section">
              <div className="batch-section-title mb-3">
                <span className="section-badge">2</span> Financials & Sourcing
              </div>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="batch-field-label">Purchase Unit Cost (₹) <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="purchase_price" 
                      value={form.purchase_price} 
                      onChange={handleChange} 
                      placeholder="e.g. 120.50"
                      required 
                      className="batch-modal-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="batch-field-label">Retail Selling Price (₹) <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="number" 
                      step="0.01" 
                      name="selling_price" 
                      value={form.selling_price} 
                      onChange={handleChange} 
                      placeholder="e.g. 150.00"
                      required 
                      className="batch-modal-input"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="batch-field-label">Initial Stock Quantity <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                  type="number" 
                  name="stock_quantity" 
                  value={form.stock_quantity} 
                  onChange={handleChange} 
                  placeholder="e.g. 500 (total single units)"
                  required 
                  className="batch-modal-input"
                />
                <div className="batch-modal-hint mt-1">Enter total quantity in single base units (e.g. total tablets, bottles).</div>
              </Form.Group>

              <Form.Group>
                <Form.Label className="batch-field-label">Select Supplier <span className="text-danger">*</span></Form.Label>
                <Form.Select 
                  name="supplier_id" 
                  value={form.supplier_id} 
                  onChange={handleChange} 
                  required
                  className="batch-modal-select"
                >
                  <option value="">Choose Supplier...</option>
                  {supplierList.map(sup => (
                    <option key={sup.supplier_id} value={sup.supplier_id}>
                      {sup.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          </Modal.Body>

          <Modal.Footer className="batch-modal-footer d-flex justify-content-end py-3 px-4">
            <Button variant="primary" type="submit" className="batch-submit-btn fw-bold px-4" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : (currentBatch ? 'Update Batch Details' : 'Add Stock Batch')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default BatchManagement;